import { Body, Box, MenuItem } from "@biom3/react";
import styled from "@emotion/styled";
import Link from "next/link";
import type { FC, ReactElement, ReactNode } from "react";

interface MinifiedMenuItemProps {
  icon: ReactElement;
  path?: string;
  isSelected?: boolean;
  onClick?: () => void;
  testId?: string;
  label?: string;
  badge?: ReactNode;
}

const MenuItemContainer = styled.div<{
  isSelected?: boolean;
}>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  background-color: ${({ isSelected }) => (isSelected ? "rgba(243, 243, 243, 0.20)" : "transparent")};
  width: 100%;
  &:hover {
    background-color: rgba(243, 243, 243, 0.15);
  }
`;

const Badge: FC<{ text: string }> = ({ text }) => (
  <MenuItem.Badge
    variant="emphasis"
    badgeContent={
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          px: "base.spacing.x1",
          py: "1px",
        }}
      >
        <Body
          size="small"
          weight="bold"
          sx={{
            fontSize: "10px",
            letterSpacing: "0.01em",
            color: "base.color.neutral.1000",
          }}
        >
          {text}
        </Body>
      </Box>
    }
    sx={{
      background: "base.color.neutral.300",
    }}
  />
);

export const MinifiedMenuItem: FC<MinifiedMenuItemProps> = ({
  icon,
  path,
  isSelected,
  onClick,
  testId,
  label,
  badge,
}) => {
  const content = (
    <MenuItemContainer
      isSelected={isSelected}
      onClick={onClick}
      data-testid={testId}
    >
      {icon}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {label && (
          <Body
            size="medium"
            weight="bold"
            sx={{
              color: "rgba(243, 243, 243, 0.9)",
              textTransform: "capitalize",
              letterSpacing: "0.01em",
              lineHeight: "1.4",
            }}
          >
            {label}
          </Body>
        )}
        {badge && (
          <Box sx={{ ml: "base.spacing.x2" }}>
            {typeof badge === "string" ? <Badge text={badge} /> : badge}
          </Box>
        )}
      </Box>
    </MenuItemContainer>
  );

  if (path) {
    return (
      <Link href={path} style={{ textDecoration: "none" }}>
        {content}
      </Link>
    );
  }

  return content;
};
